// Is this device online, and does the server answer? `navigator.onLine` plus the
// online/offline events, the time the connection dropped (for "Offline since 23:56."), and
// the server's reachability as the API client last saw it (UI0A-201). While the server is
// unreachable, /health is probed every few seconds so the indicator recovers by itself.

import { useSyncExternalStore } from 'react';
import { apiBaseUrl } from '../lib/api.js';
import { isServerReachable, onReachabilityChange, reportReachable } from '../lib/reachability.js';

/** `connected`: online and the server answers. `unreachable`: online, server silent. */
export type ConnectionStatus = 'connected' | 'unreachable' | 'offline';

export interface ConnectionState {
  online: boolean;
  /** When the device went offline (undefined while online, or if it started offline). */
  since?: Date;
  /** Whether the server answered the last request (or probe). */
  reachable: boolean;
  status: ConnectionStatus;
}

/** How often an unreachable server is probed. */
export const PROBE_INTERVAL_MS = 10_000;

const statusOf = (online: boolean, reachable: boolean): ConnectionStatus =>
  !online ? 'offline' : reachable ? 'connected' : 'unreachable';

const initialOnline = typeof navigator === 'undefined' ? true : navigator.onLine;
let state: ConnectionState = {
  online: initialOnline,
  reachable: isServerReachable(),
  status: statusOf(initialOnline, isServerReachable()),
};
const listeners = new Set<() => void>();

function set(next: { online: boolean; since?: Date; reachable: boolean }) {
  if (next.online === state.online && next.reachable === state.reachable) return;
  state = { ...next, status: statusOf(next.online, next.reachable) };
  listeners.forEach((l) => l());
  scheduleProbe();
}

function updateOnline(online: boolean) {
  if (online === state.online) return;
  set(
    online
      ? { online, reachable: state.reachable }
      : { online, since: new Date(), reachable: state.reachable },
  );
  // Back online: find out straight away whether the server answers too.
  if (online) void probe();
}

function updateReachable() {
  set({ online: state.online, since: state.since, reachable: isServerReachable() });
}

/** Asks the server's health endpoint; any answer counts as reachable. */
export async function probe(): Promise<void> {
  try {
    await fetch(`${apiBaseUrl()}/health`, { cache: 'no-store' });
    reportReachable(true);
  } catch {
    reportReachable(false);
  }
}

let probeTimer: ReturnType<typeof setTimeout> | undefined;
function scheduleProbe() {
  clearTimeout(probeTimer);
  probeTimer = undefined;
  if (listeners.size === 0 || !state.online || state.reachable) return;
  probeTimer = setTimeout(() => {
    probeTimer = undefined;
    void probe().finally(scheduleProbe);
  }, PROBE_INTERVAL_MS);
}

const onOnline = () => updateOnline(true);
const onOffline = () => updateOnline(false);
let unsubscribeReachability: (() => void) | undefined;

function subscribe(listener: () => void) {
  if (listeners.size === 0) {
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    unsubscribeReachability = onReachabilityChange(updateReachable);
    updateOnline(navigator.onLine);
    updateReachable();
  }
  listeners.add(listener);
  scheduleProbe();
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
      unsubscribeReachability?.();
      clearTimeout(probeTimer);
    }
  };
}

/** The device's connection state; re-renders when it changes. */
export function useConnection(): ConnectionState {
  return useSyncExternalStore(
    subscribe,
    () => state,
    () => state,
  );
}
